import { gql } from '@apollo/client';
import { Tooltip } from '@mui/material';
import { GetServerSideProps, NextPage } from 'next';
import { ConnectorsGrid } from '.';
import {
  BackButton,
  ConnectorCard,
  ConnectorCardContent,
  ConnectorCardDescription,
  ConnectorCardLogo,
  ConnectorCardSubTitle,
  ConnectorCardTag,
  ConnectorCardTags,
  ConnectorCardTitle,
  OtherConnectors,
  OtherConnectorsTitle,
  PageContainer,
  ProductSection,
} from '../../components/connectors/styled';
import { FOOTER_QUERY } from '../../components/footer';
import { DarkFooter } from '../../components/footer/darkFooter';
import { HEADER_QUERY, Header } from '../../components/header';
import { ResponsiveContainer } from '../../components/responsive';
import { Icon } from '../../components/ui/icon';
import { ContentStackClient } from '../../lib/contentstack';
import { getTheme } from '../../theme/getTheme';
import { ClibConnector } from '../../types/contentstack';
import { IPageProps } from '../../types/page';
import { getAssetUrl, renderRichText } from '../../utils/contentstack';
import { fetchConnectors } from '../api/connectors';

type ConnectorDetailsPageProps = IPageProps<unknown> & {
  connector: ClibConnector;
  relatedConnectors: ClibConnector[];
};

const Page: NextPage<ConnectorDetailsPageProps> = ({
  header,
  footer,
  connector,
  relatedConnectors,
}) => {
  const theme = getTheme();

  return (
    <>
      <Header
        pageTitle={`Connectors | ${connector?.title}`}
        pageDescription={'Library'}
        {...header}
      />
      <PageContainer>
        <ResponsiveContainer style={{ marginTop: 80, marginBottom: 16 }}>
          <BackButton href="/connectors">
            <Icon
              name="arrow-left"
              color="#fff"
              size={18}
              style={{ marginRight: 8 }}
            />
            <span>Back to connectors</span>
          </BackButton>
        </ResponsiveContainer>

        <ResponsiveContainer style={{ marginBottom: 40 }}>
          <ProductSection>
            <ConnectorCard>
              <ConnectorCardLogo>
                <img
                  src={getAssetUrl(connector?.logoConnection)}
                  alt={connector?.title!}
                />
              </ConnectorCardLogo>

              <ConnectorCardContent>
                <ConnectorCardTitle
                  style={{ fontSize: 48, fontWeight: 700, marginBottom: 0 }}
                >
                  {connector?.title}
                </ConnectorCardTitle>

                <ConnectorCardTags style={{ marginBottom: 16 }}>
                  {!!connector?.availability &&
                    connector?.availability?.toLowerCase() !==
                      'general availability' && (
                      <Tooltip
                        title="This connector will be publicly available soon"
                        placement="top"
                        arrow={true}
                      >
                        <ConnectorCardTag
                          style={{
                            color: theme.colors.eb2,
                            fontSize: 14,
                          }}
                        >
                          {connector?.availability}
                        </ConnectorCardTag>
                      </Tooltip>
                    )}

                  {!!connector?.edge_y_n && (
                    <Tooltip
                      title="Requires Edge Gateway and Pro plan"
                      placement="top"
                      arrow={true}
                    >
                      <ConnectorCardTag
                        style={{
                          color: theme.colors.yellow,
                          fontSize: 14,
                        }}
                      >
                        Edge
                      </ConnectorCardTag>
                    </Tooltip>
                  )}

                  {connector?.categoriesConnection?.edges?.map((category) => (
                    <ConnectorCardTag
                      key={category?.node?.title}
                      style={{ fontSize: 14 }}
                    >
                      {category?.node?.title}
                    </ConnectorCardTag>
                  ))}
                </ConnectorCardTags>

                <ConnectorCardDescription
                  dangerouslySetInnerHTML={{
                    __html: renderRichText(connector?.mapped_description?.json),
                  }}
                />

                <ConnectorCardSubTitle>Use cases:</ConnectorCardSubTitle>

                <div
                  style={{ color: '#b4b4b3' }}
                  dangerouslySetInnerHTML={{
                    __html: renderRichText(connector?.use_cases?.json),
                  }}
                />
              </ConnectorCardContent>
            </ConnectorCard>
          </ProductSection>
        </ResponsiveContainer>

        {!!relatedConnectors?.length && (
          <ResponsiveContainer style={{ marginTop: 90, marginBottom: 16 }}>
            <OtherConnectors>
              <OtherConnectorsTitle>
                Explore more connectors in this category
              </OtherConnectorsTitle>

              <ConnectorsGrid connectors={relatedConnectors} />
            </OtherConnectors>
          </ResponsiveContainer>
        )}
      </PageContainer>

      <DarkFooter withAngle={false} links={footer?.links} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const [{ data: pageData }, connectors] = await Promise.all([
      ContentStackClient().query<IPageProps<any>>({ query: PAGE_QUERY }),

      fetchConnectors({ url: context.resolvedUrl }),
    ]);

    const connector = connectors?.[0];

    if (!connector) {
      return { props: {}, notFound: true };
    }

    const relatedConnectors = await fetchConnectors({
      OR: connector?.categoriesConnection?.edges?.map((category) => ({
        categories: {
          clib_category: {
            uid: category?.node?.system?.uid,
          },
        },
      })),
    });

    return {
      props: {
        ...pageData,

        connector,

        relatedConnectors: relatedConnectors
          ?.filter((c) => c?.url !== connector?.url)
          .slice(0, 6),
      },
    };
  } catch (e) {
    return { props: {} };
  }
};

const PAGE_QUERY = gql`
  query connectorPageQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}
  }
`;

export default Page;
